import React, { useEffect, useMemo, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, FieldGroup, Input, Notification } from '@xsolla/ui-core';
import { Trash } from '@xsolla/ui-icons';
import { FileUploader, UploaderValue } from '@xsolla/ui-uploader';

import { getAccountManager, SocialConnection } from '../../../api';
import { FieldTypes } from '../../../api/configurations/social/types';
import { AccountManager } from '../../../api/merchant/types';
import {
  CLIENT_SECRET_MASKED_STRING,
  DEFAULT_ACCOUNT_MANAGER,
  DEFAULT_XSOLLA_ACCOUNT_VALUES
} from '../../../constants';
import { useConfig } from '../../../contexts/config';
import { useNotifications } from '../../../hooks/useNotifications';
import { getRandomString } from '../../../utils';
import { FieldSwitch, LinkFieldArray, LinkStylesUnderline, Notice, Preloader } from '../../ui';
import { CheckBoxBlock } from './CheckBoxBlock';
import scopeValues, { CONTACT_ACCOUNT_MANAGER } from './scopeValues';
import {
  MainContentBox,
  NotificationCustom,
  SyncUserWrapper,
  ThinContentBox,
  WrapperNotification
} from './style';
import { DocumentationLink, FieldNames, UploadFileProps } from './types';
import { clientIdMaxLength } from './validator';

type Props = {
  socialSettings: SocialConnection;
};

const SocialSettingsMainBox = ({ socialSettings }: Props): JSX.Element => {
  const intl = useIntl();
  const { merchantId } = useConfig();
  const { values } = useFormState();
  const { change } = useForm();
  const { client_id, client_secret } = values;
  const { provider, configurable_fields } = socialSettings;
  const { showErrorNotify } = useNotifications();

  const [accountManager, setAccountManager] = useState<AccountManager>(DEFAULT_ACCOUNT_MANAGER);
  const [clientSecretHasValue, setClientSecretHasValue] = useState<boolean>(false);

  useEffect(() => {
    getAccountManager(merchantId)
      .then((res) => {
        if (res.email) {
          setAccountManager(res);
        }
      })
      .catch((error) => {
        showErrorNotify(error);
      });
  }, []);

  useEffect(() => {
    configurable_fields.forEach((item) => {
      if (item.name === FieldNames.CLIENT_SECRET && item.value) {
        setClientSecretHasValue(true);
      }
    });
  }, [configurable_fields]);

  const newConfigurableFields = useMemo(() => {
    const newState = configurable_fields.map((item) => {
      if (item.name === 'audience') {
        return { ...item, type: FieldTypes.STRING };
      }
      return item;
    });

    return newState;
  }, [configurable_fields]);

  const scopes = useMemo(() => scopeValues[provider] || {}, [provider]);

  const isXsollaKeys = useMemo(() => {
    if (provider === 'xsolla-id') return false;
    if (provider === 'steam') return !client_id;
    return !client_id || !client_secret;
  }, [provider, client_id, client_secret]);

  const placeholder = (name: string) =>
    intl.formatMessage({
      id: `social-connection.settings.field.${provider}.${name}.placeholder`,
      defaultMessage: intl.messages[`social-connection.settings.field.${name}.placeholder`]
    });

  const renderUserData = useMemo(() => {
    // TODO: Remove this once OAuth2 scopes behavior is finalized; currently hiding scopes until clarified
    if (
      (provider === 'babka' || provider === 'xsolla-id') &&
      DEFAULT_XSOLLA_ACCOUNT_VALUES.IS_SCOPES_HIDDEN
    ) {
      return null;
    }

    return (
      <CheckBoxBlock
        name="user-data"
        title={intl.messages['social-connection.settings.user-data.title']}
        subtitle={intl.formatMessage(
          { id: 'social-connection.settings.user-data.subtitle' },
          {
            name:
              provider &&
              intl.formatMessage({
                id: `auth-methods.${provider}`
              })
          }
        )}
        provider={provider}
        scopes={scopes?.userData || []}
        disabled={isXsollaKeys}
      />
    );
  }, [scopes, isXsollaKeys]);

  const renderPermissions = useMemo(() => {
    if (
      (provider === 'babka' || provider === 'xsolla-id') &&
      DEFAULT_XSOLLA_ACCOUNT_VALUES.IS_SCOPES_HIDDEN
    ) {
      return null;
    }

    return (
      <CheckBoxBlock
        name="permission"
        title={intl.messages['social-connection.settings.permission.title']}
        subtitle={intl.formatMessage(
          { id: 'social-connection.settings.permission.subtitle' },
          {
            name:
              provider &&
              intl.formatMessage({
                id: `auth-methods.${provider}`
              })
          }
        )}
        provider={provider}
        scopes={scopes?.permissions || []}
        disabled={isXsollaKeys}
        permissionDocLink={scopes?.permissionDocLink || ''}
      />
    );
  }, [scopes, isXsollaKeys]);

  const uploadFile = async ({ name, file, callback }: UploadFileProps): Promise<UploaderValue> => {
    try {
      const text = await file.text();
      callback(text);
      return {
        filename: intl.formatMessage({
          id: `social-connection.settings.field.${provider}.${name}.file`,
          defaultMessage: intl.messages[`social-connection.settings.field.${name}.file`]
        }),
        url: file.name
      };
    } catch (e) {
      showErrorNotify();
    }

    return {
      filename: '',
      url: ''
    };
  };

  const deleteFile = async (name: string) => {
    change(name, null);
  };

  const deleteValue = async (name: string) => {
    change(name, null);
    setClientSecretHasValue(false);
  };

  const renderConfigurableFields = useMemo(() => {
    return (
      <>
        {newConfigurableFields.map(({ name, type, required, typeMetadata }) => {
          if (socialSettings.provider === 'babka' && name === 'custom_return_url') {
            return <></>;
          }

          if (
            socialSettings.provider === 'xsolla-id' &&
            (name === 'client_id' || name === 'client_secret' || name === 'custom_return_url')
          ) {
            return <></>;
          }

          const ButtonDeleteLink = ({
            disabled,
            onClick
          }: {
            disabled: boolean;
            onClick: () => void;
          }) => {
            return (
              <Button
                look="icon"
                palette="brand"
                size="md"
                onClick={() => onClick()}
                disabled={disabled}>
                <Trash />
              </Button>
            );
          };

          return (
            <FieldGroup
              key={getRandomString()}
              label={intl.formatMessage({
                id: `social-connection.settings.field.${provider}.${name}.label`,
                defaultMessage: intl.messages[`social-connection.settings.field.${name}.label`]
              })}
              footer={
                type === FieldTypes.STRING &&
                name === FieldNames.CLIENT_SECRET &&
                intl.formatMessage({
                  id: `social-connection.settings.field.${provider}.${name}.footer`,
                  defaultMessage: intl.messages[`social-connection.settings.field.${name}.footer`]
                })
              }
              size="md">
              <Field name={name}>
                {({ input: { value, onChange, ...input }, meta }) => {
                  if (type === FieldTypes.ARRAY) {
                    return (
                      <LinkFieldArray
                        {...input}
                        label=""
                        addLabel={intl.formatMessage({
                          id: 'configuration-page.urls.callback-urls.input.add-button'
                        })}
                        placeholder={intl.formatMessage({
                          id: 'configuration-page.urls.callback-urls.input.placeholder'
                        })}
                        errorMessage={
                          meta.touched && meta.error && !Array.isArray(meta.error)
                            ? intl.formatMessage({ id: meta.error })
                            : ''
                        }
                      />
                    );
                  }

                  if (type === FieldTypes.FILE) {
                    return (
                      <FileUploader
                        {...input}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        accept={typeMetadata.acceptFileType || ''}
                        multiple={false}
                        errorMessage={
                          meta.touched && meta.error ? intl.formatMessage({ id: meta.error }) : ''
                        }
                        options={{
                          uploadFn: (file) => uploadFile({ name, file, callback: onChange }),
                          deleteFn: () => deleteFile(name)
                        }}
                        value={{
                          filename: value
                            ? intl.formatMessage({
                                id: `social-connection.settings.field.${provider}.${name}.file`,
                                defaultMessage:
                                  intl.messages[`social-connection.settings.field.${name}.file`]
                              })
                            : '',
                          url: value
                        }}
                        placeholder={required ? '' : placeholder(input.name)}
                      />
                    );
                  }

                  if (name === FieldNames.CLIENT_SECRET) {
                    return (
                      <Input
                        extraAlign="right"
                        extraAllowClick
                        errorMessage={
                          meta.touched && meta.error
                            ? intl.formatMessage({ id: meta.error }, { number: clientIdMaxLength })
                            : ''
                        }
                        name={input.name}
                        value={clientSecretHasValue ? CLIENT_SECRET_MASKED_STRING : value}
                        onChange={onChange}
                        placeholder={
                          required ||
                          input.name === 'url_authorize' ||
                          input.name === 'url_access_token'
                            ? ''
                            : placeholder(input.name)
                        }
                        extra={() => (
                          <ButtonDeleteLink disabled={!value} onClick={() => deleteValue(name)} />
                        )}
                        type={name === FieldNames.CLIENT_SECRET ? 'password' : ''}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    );
                  }

                  return (
                    <Input
                      errorMessage={
                        meta.touched && meta.error
                          ? intl.formatMessage({ id: meta.error }, { number: clientIdMaxLength })
                          : ''
                      }
                      name={input.name}
                      value={value}
                      onChange={onChange}
                      placeholder={
                        required ||
                        input.name === 'url_authorize' ||
                        input.name === 'url_access_token'
                          ? ''
                          : placeholder(input.name)
                      }
                      type=""
                      autoComplete="off"
                      spellCheck={false}
                    />
                  );
                }}
              </Field>
            </FieldGroup>
          );
        })}
      </>
    );
  }, [newConfigurableFields, clientSecretHasValue]);

  if (!provider) {
    return <Preloader />;
  }

  return (
    <>
      <MainContentBox hideBorder={provider === 'xsolla-id'}>
        <ThinContentBox>
          {provider !== 'xsolla-id' && (
            <WrapperNotification>
              <Notification palette="brand" icon={Notice}>
                {provider === 'babka' ? (
                  <FormattedMessage
                    id="social-connection.settings.babka.app-id.notification"
                    values={{
                      a: (chunk: string) => (
                        <NotificationCustom
                          href={DocumentationLink.BABKA}
                          target="_blank"
                          rel="noreferrer">
                          {chunk}
                        </NotificationCustom>
                      )
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id={
                      scopes.appIDDocLink === CONTACT_ACCOUNT_MANAGER
                        ? 'app.contact-integrator.notification'
                        : `social-connection.settings.${provider}.app-id.notification`
                    }
                    defaultMessage={
                      intl.messages['social-connection.settings.app-id.notification']
                    }>
                    {(chunk) => (
                      <LinkStylesUnderline>
                        <a
                          href={
                            scopes.appIDDocLink === CONTACT_ACCOUNT_MANAGER
                              ? `mailto:${accountManager.email}`
                              : scopes.appIDDocLink
                          }
                          data-test-id="app_ID_link"
                          target="_blank"
                          rel="noreferrer">
                          {chunk}
                        </a>
                      </LinkStylesUnderline>
                    )}
                  </FormattedMessage>
                )}
              </Notification>
            </WrapperNotification>
          )}

          {renderConfigurableFields}
        </ThinContentBox>
      </MainContentBox>

      {renderUserData}

      {renderPermissions}

      {provider !== 'xsolla-id' && (
        <SyncUserWrapper>
          <ThinContentBox>
            <WrapperNotification>
              <Notification palette="brand" icon={Notice}>
                {intl.messages['social-connection.settings.sync.notification']}
              </Notification>
            </WrapperNotification>
            <FieldSwitch
              name="refresh_always"
              data-test-id="refresh_always"
              disabled={isXsollaKeys}
              caption={intl.formatMessage({
                id: 'social-connection.settings.sync.switch.subtitle'
              })}
              label={intl.formatMessage({ id: 'social-connection.settings.sync.switch.title' })}
            />
          </ThinContentBox>
        </SyncUserWrapper>
      )}
    </>
  );
};

export default SocialSettingsMainBox;
